<template>
  <div class="pp-preview" :class="`pp-preview--${mode}`">
    <div ref="stage" class="pp-preview__stage">
      <!-- Computador: janela de navegador com a pagina em 1440 reduzida -->
      <div v-if="mode === 'desktop'" class="pp-preview__browser">
        <div class="pp-preview__browser-bar">
          <span class="pp-preview__dots"><i /><i /><i /></span>
          <span class="pp-preview__address">{{ address }}</span>
        </div>
        <div ref="viewport" class="pp-preview__viewport">
          <iframe
            v-if="src"
            ref="frame"
            class="pp-preview__frame"
            :src="src"
            title="Prévia do seu perfil público"
            :style="desktopFrameStyle"
            @load="onLoad"
          />
        </div>
      </div>

      <!-- Celular: moldura de telefone 375x812 -->
      <div v-else-if="mode === 'mobile'" class="pp-preview__phone-wrap" :style="phoneWrapStyle">
        <div class="pp-preview__phone" :style="phoneStyle">
          <span class="pp-preview__notch" />
          <iframe
            v-if="src"
            ref="frame"
            class="pp-preview__frame pp-preview__frame--phone"
            :src="src"
            title="Prévia do seu perfil público no celular"
            @load="onLoad"
          />
        </div>
      </div>

      <!-- Tela pequena: a prévia ocupa a area toda, na largura do aparelho -->
      <div v-else class="pp-preview__fill">
        <iframe
          v-if="src"
          ref="frame"
          class="pp-preview__frame pp-preview__frame--fill"
          :src="src"
          title="Prévia do seu perfil público"
          @load="onLoad"
        />
      </div>

      <div v-if="!src || !everLoaded" class="pp-preview__state">
        <template v-if="error">
          <p>Não conseguimos carregar a prévia agora.</p>
          <button type="button" class="pp-btn pp-btn--small" @click="retryPreview">
            Tentar de novo
          </button>
        </template>
        <p v-else>Carregando a prévia...</p>
      </div>
    </div>
    <p v-if="mode === 'mobile'" class="pp-preview__caption">
      <smartphone-icon size="14" /> Como aparece no celular
    </p>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { SmartphoneIcon } from "vue-feather-icons";
import service, { editorPreviewUrl, errorReason, errorStatus, profileOrigin } from "@/services/publicProfile";
import { trackPublicProfile } from "./tracking";

const DESKTOP_WIDTH = 1440;
const PHONE_WIDTH = 375;
const PHONE_HEIGHT = 812;
const PHONE_BEZEL = 12;
// Renova o token um pouco antes de vencer (token de 30 min).
const RENEW_BEFORE_MS = 60 * 1000;

export default {
  name: "ProfilePreview",
  components: { SmartphoneIcon },
  props: {
    mode: { type: String, default: "desktop" }, // desktop | mobile | fill
    address: { type: String, default: "" },
  },
  data: () => ({
    token: null,
    loaded: false,
    everLoaded: false,
    src: "",
    error: false,
    stageWidth: 0,
    stageHeight: 0,
    viewportWidth: 0,
    viewportHeight: 0,
  }),
  computed: {
    ...mapState("publicProfile", ["draft", "savedVersion"]),
    defaultName() {
      const page = this.$store.getters["publicProfile/page"];
      return (page && page.default_name) || "";
    },
    // Escolhas atuais do editor, inclusive as ainda nao salvas.
    overrides() {
      if (!this.draft) return null;
      return {
        template: this.draft.template,
        theme: this.draft.theme,
        sections: this.draft.sections,
        // Nome já resolvido: o escolhido ou, vazio, o do cadastro (a prévia não sabe qual é).
        name: (this.draft.display_name || "").trim() || this.defaultName || undefined,
        headline: this.draft.headline || null,
        tagline: this.draft.tagline || null,
        hideBranding: !!this.draft.hide_branding,
        hideResumeDownload: !!this.draft.hide_resume_download,
        hideShare: !!this.draft.hide_share,
        showExperienceDetails: !!this.draft.show_experience_details,
        character: this.draft.character || null,
        gameOnly: !!this.draft.game_only,
        platformerScene: this.draft.platformer_scene || null,
        presentationBackdrop: this.draft.presentation_backdrop || null,
        presentationPace: this.draft.presentation_pace || null,
      };
    },
    desktopScale() {
      return this.viewportWidth ? this.viewportWidth / DESKTOP_WIDTH : 0.5;
    },
    desktopFrameStyle() {
      const scale = this.desktopScale;
      return {
        width: `${DESKTOP_WIDTH}px`,
        height: `${Math.ceil(this.viewportHeight / scale) || 900}px`,
        transform: `scale(${scale})`,
      };
    },
    phoneScale() {
      const fullHeight = PHONE_HEIGHT + PHONE_BEZEL * 2;
      const fullWidth = PHONE_WIDTH + PHONE_BEZEL * 2;
      if (!this.stageHeight) return 1;
      return Math.min(1, (this.stageHeight - 16) / fullHeight, (this.stageWidth - 16) / fullWidth);
    },
    phoneStyle() {
      return {
        width: `${PHONE_WIDTH + PHONE_BEZEL * 2}px`,
        height: `${PHONE_HEIGHT + PHONE_BEZEL * 2}px`,
        padding: `${PHONE_BEZEL}px`,
        transform: `scale(${this.phoneScale})`,
      };
    },
    phoneWrapStyle() {
      return {
        width: `${(PHONE_WIDTH + PHONE_BEZEL * 2) * this.phoneScale}px`,
        height: `${(PHONE_HEIGHT + PHONE_BEZEL * 2) * this.phoneScale}px`,
      };
    },
  },
  // A previa carrega uma vez (src fixo). Depois, cada mudanca vai por postMessage e o
  // front publico redesenha no navegador, sem recarregar a pagina.
  watch: {
    overrides: {
      deep: true,
      handler() {
        this.postUpdate();
      },
    },
    // Salvou: itens escondidos e dados so valem depois de salvos; a previa busca de novo.
    savedVersion() {
      this.post({ type: "profile-preview:reload" });
    },
    // Trocar de aparelho recria o iframe: monta a URL de novo com o token atual
    // (o primeiro pode ter vencido) e as escolhas de agora.
    mode() {
      this.loaded = false;
      this.buildSrc();
      this.$nextTick(this.measure);
    },
  },
  mounted() {
    window.addEventListener("message", this.onMessage);
    this.loadToken();
    this.measure();
    if (typeof window.ResizeObserver === "function") {
      this.observer = new window.ResizeObserver(() => this.measure());
      this.observer.observe(this.$refs.stage);
    }
    window.addEventListener("resize", this.measure);
  },
  beforeDestroy() {
    this.destroyed = true;
    window.removeEventListener("message", this.onMessage);
    cancelAnimationFrame(this.frameRequest);
    clearTimeout(this.renewTimer);
    if (this.observer) this.observer.disconnect();
    window.removeEventListener("resize", this.measure);
  },
  methods: {
    async loadToken() {
      this.error = false;
      clearTimeout(this.renewTimer);
      try {
        const { token, expires_at: expiresAt } = await service.previewToken();
        if (this.destroyed) return;
        if (!token) throw new Error("token vazio");
        const firstToken = !this.src;
        this.token = token;
        // Primeiro token monta o iframe (ja com as escolhas atuais na URL, para o
        // primeiro desenho sair certo). Renovacao avisa a previa aberta; a URL
        // nova so vale quando o iframe for recriado (troca de aparelho).
        if (firstToken) this.buildSrc();
        else this.post({ type: "profile-preview:reload", token });
        const expires = expiresAt ? new Date(expiresAt).getTime() : Date.now() + 30 * 60 * 1000;
        const wait = Math.max(30 * 1000, expires - Date.now() - RENEW_BEFORE_MS);
        clearTimeout(this.renewTimer);
        this.renewTimer = setTimeout(this.loadToken, wait);
      } catch (e) {
        if (!this.destroyed) {
          this.error = true;
          trackPublicProfile(this.$store.state.publicProfile, "public_profile_preview_failed", {
            mode: this.mode,
            renewal: !!this.src,
            status: errorStatus(e),
            reason: errorReason(e) || (e && e.message) || null,
          });
        }
      }
    },
    retryPreview() {
      trackPublicProfile(this.$store.state.publicProfile, "public_profile_preview_retry_clicked", { mode: this.mode });
      this.loadToken();
    },
    buildSrc() {
      if (!this.token) return;
      this.src = editorPreviewUrl(this.token, this.overrides, this.savedVersion);
    },
    onLoad() {
      this.loaded = true;
      this.everLoaded = true;
      this.measure();
    },
    post(message) {
      const frame = this.$refs.frame;
      if (frame && frame.contentWindow) frame.contentWindow.postMessage(message, profileOrigin());
    },
    // Um envio por quadro: arrastar ou digitar nao inunda a previa.
    postUpdate() {
      cancelAnimationFrame(this.frameRequest);
      this.frameRequest = requestAnimationFrame(() => {
        if (this.overrides) this.post({ type: "profile-preview:update", overrides: this.overrides });
      });
    },
    onMessage(event) {
      if (event.origin !== profileOrigin()) return;
      if (event.data && event.data.type === "profile-preview:ready") this.postUpdate();
    },
    measure() {
      const stage = this.$refs.stage;
      if (stage) {
        this.stageWidth = stage.clientWidth;
        this.stageHeight = stage.clientHeight;
      }
      const viewport = this.$refs.viewport;
      if (viewport) {
        this.viewportWidth = viewport.clientWidth;
        this.viewportHeight = viewport.clientHeight;
      }
    },
  },
};
</script>

<style lang="scss">
.pp-preview {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 0;
  font-family: Roboto, sans-serif;

  &__stage {
    position: relative;
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: center;
    min-height: 0;
  }

  &__browser {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    overflow: hidden;
    border-radius: 12px;
    background: #fff;
    box-shadow: 0 4px 24px rgba(9, 30, 66, 0.12);
  }

  &__browser-bar {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    height: 36px;
    border-bottom: 0.5px solid #dfe1e6;
    background: #f4f5f7;
  }

  &__dots {
    position: absolute;
    left: 14px;
    display: flex;
    gap: 6px;

    i {
      width: 10px;
      height: 10px;
      border-radius: 9999px;
      background: #ff5f57;

      &:nth-child(2) {
        background: #febc2e;
      }

      &:nth-child(3) {
        background: #28c840;
      }
    }
  }

  &__address {
    color: #4f4f4f;
    font-size: 12px;
  }

  &__viewport {
    position: relative;
    flex: 1;
    min-height: 0;
    overflow: hidden;
  }

  &__frame {
    display: block;
    border: 0;
    background: #fff;

    &:not(.pp-preview__frame--phone):not(.pp-preview__frame--fill) {
      position: absolute;
      top: 0;
      left: 0;
      transform-origin: 0 0;
    }

    &--phone {
      width: 100%;
      height: 100%;
      border-radius: 28px;
    }

    &--fill {
      width: 100%;
      height: 100%;
    }
  }

  &__phone-wrap {
    position: relative;
  }

  &__phone {
    position: absolute;
    top: 0;
    left: 0;
    border-radius: 44px;
    background: #06152b;
    transform-origin: 0 0;
  }

  &__notch {
    position: absolute;
    top: 20px;
    left: 50%;
    z-index: 1;
    width: 104px;
    height: 24px;
    border-radius: 9999px;
    background: #06152b;
    transform: translateX(-50%);
  }

  &__fill {
    width: 100%;
    height: 100%;
    overflow: hidden;
    border-radius: 16px;
    background: #fff;
    box-shadow: 0 4px 16px rgba(9, 30, 66, 0.12);
  }

  &__state {
    position: absolute;
    top: 50%;
    left: 50%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 16px 20px;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.95);
    color: #4f4f4f;
    font-size: 14px;
    text-align: center;
    transform: translate(-50%, -50%);
  }

  &__caption {
    display: inline-flex;
    align-self: center;
    align-items: center;
    gap: 6px;
    margin-top: 12px;
    padding: 4px 12px;
    border-radius: 9999px;
    background: #fff;
    color: #4f4f4f;
    font-size: 12px;
  }
}
</style>
